/**
 * Small pieces the administration panels share.
 *
 * Kept here rather than in the global UI kit because they encode admin-specific
 * meaning — a "controlled" badge, an inline save row — that the rest of the
 * application has no use for.
 */
import type { ReactNode } from 'react';

export function StatusPill({ status }: { status: string }) {
  const tone =
    status === 'ACTIVE'
      ? 'bg-emerald-100 text-emerald-800'
      : status === 'DISABLED' || status === 'RETIRED'
        ? 'bg-slate-200 text-slate-700'
        : 'bg-amber-100 text-amber-900';
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${tone}`}
    >
      {status.toLowerCase()}
    </span>
  );
}

/**
 * Marks content Sales and AI may not originate or alter.
 *
 * Worth a visible badge and not only an enforcement rule: an administrator
 * editing a metrics line should be able to see, without reading the docs, that
 * they are the only person who can.
 */
export function ControlledBadge({ reason }: { reason?: string }) {
  return (
    <span
      className="rounded-full bg-teal/10 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-teal"
      title={reason ?? 'Corporate claim — administrator-controlled'}
    >
      Controlled
    </span>
  );
}

export function AdminTable({
  headers,
  children,
  minWidth = 640,
}: {
  headers: string[];
  children: ReactNode;
  minWidth?: number;
}) {
  return (
    <div className="-mx-5 overflow-x-auto">
      <table
        className="w-full border-collapse text-[13px]"
        style={{ minWidth: `${minWidth}px` }}
      >
        <thead>
          <tr className="border-b border-rule text-left text-[11px] uppercase tracking-wide text-muted">
            {headers.map((header) => (
              <th key={header} className="px-5 py-2 font-medium">
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-rule">{children}</tbody>
      </table>
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="px-5 py-6 text-[13px] text-muted">{children}</p>;
}
